import React, { useState, useRef, useCallback, useEffect } from 'react';
import WarHeader from './WarHeader';

const SWIPE_THRESHOLD = 40;
const VELOCITY_THRESHOLD = 0.3; // px/ms — fast flick detection

export default function WarSwiper({ t, wars }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const containerRef = useRef(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const startTime = useRef(0);
  const isHorizontalSwipe = useRef(null);

  // Reset index when wars array changes
  useEffect(() => { setCurrentIndex(0); }, [wars.length]);

  const canGoNext = currentIndex < wars.length - 1;   // next = older (right arrow)
  const canGoPrev = currentIndex > 0;                  // prev = newer (left arrow)

  const goTo = useCallback((newIndex) => {
    if (newIndex < 0 || newIndex >= wars.length) return;
    setIsAnimating(true);
    setCurrentIndex(newIndex);
    setDragOffset(0);
    setIsDragging(false);
  }, [wars.length]);

  const goNext = useCallback(() => {
    if (canGoNext) goTo(currentIndex + 1);
  }, [canGoNext, currentIndex, goTo]);

  const goPrev = useCallback(() => {
    if (canGoPrev) goTo(currentIndex - 1);
  }, [canGoPrev, currentIndex, goTo]);

  // ── Pointer handlers ──
  const onPointerDown = useCallback((e) => {
    if (wars.length <= 1 || isAnimating) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    startX.current = clientX;
    startY.current = clientY;
    startTime.current = Date.now();
    isHorizontalSwipe.current = null;
    setIsDragging(true);
    setIsAnimating(false);
  }, [wars.length, isAnimating]);

  const onPointerMove = useCallback((e) => {
    if (!isDragging) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const deltaX = clientX - startX.current;
    const deltaY = clientY - startY.current;

    // Determine swipe direction on first significant movement
    if (isHorizontalSwipe.current === null) {
      if (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8) {
        isHorizontalSwipe.current = Math.abs(deltaX) > Math.abs(deltaY);
        if (!isHorizontalSwipe.current) {
          setIsDragging(false);
          setDragOffset(0);
          return;
        }
      } else {
        return;
      }
    }

    if (!isHorizontalSwipe.current) return;
    if (e.cancelable) e.preventDefault();

    // Rubber-band at boundaries — diminishing resistance
    let clampedDelta = deltaX;
    if (deltaX > 0 && !canGoPrev) {
      clampedDelta = Math.sign(deltaX) * Math.pow(Math.abs(deltaX), 0.6);
    } else if (deltaX < 0 && !canGoNext) {
      clampedDelta = Math.sign(deltaX) * Math.pow(Math.abs(deltaX), 0.6);
    }

    setDragOffset(clampedDelta);
  }, [isDragging, canGoNext, canGoPrev]);

  const onPointerUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    const elapsed = Date.now() - startTime.current;
    const velocity = Math.abs(dragOffset) / Math.max(elapsed, 1);
    const isFastFlick = velocity > VELOCITY_THRESHOLD;

    // Swipe left (negative drag) → go to next (older) card
    // Swipe right (positive drag) → go to previous (newer) card
    if (dragOffset < -SWIPE_THRESHOLD && canGoNext && (isFastFlick || Math.abs(dragOffset) > SWIPE_THRESHOLD)) {
      goNext();
    } else if (dragOffset > SWIPE_THRESHOLD && canGoPrev && (isFastFlick || Math.abs(dragOffset) > SWIPE_THRESHOLD)) {
      goPrev();
    } else {
      setIsAnimating(true);
      setDragOffset(0);
    }
  }, [isDragging, dragOffset, canGoNext, canGoPrev, goNext, goPrev]);

  // ── Mouse event wiring (drag beyond element) ──
  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => onPointerMove(e);
    const handleMouseUp = () => onPointerUp();
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onPointerMove, onPointerUp]);

  // ── Keyboard navigation ──
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goNext, goPrev]);

  // Clear animating flag after transition ends
  const onTransitionEnd = useCallback(() => { setIsAnimating(false); }, []);

  if (!wars || wars.length === 0) {
    return <WarHeader t={t} war={null} />;
  }

  if (wars.length === 1) {
    return (
      <div className="war-swiper war-swiper-single">
        <WarHeader t={t} war={wars[0]} />
      </div>
    );
  }

  const trackTransform = `translateX(calc(${-currentIndex * 100}% + ${dragOffset}px))`;
  const trackTransition = isDragging ? 'none' : 'transform 0.45s cubic-bezier(0.32, 0.72, 0, 1)';

  return (
    <div className="war-swiper" ref={containerRef}>
      <div className="war-swiper-viewport">
        {/* Left arrow — go to previous (newer) */}
        <button
          className={`war-swiper-arrow war-swiper-arrow-left${!canGoPrev ? ' war-swiper-arrow-hidden' : ''}`}
          onClick={goPrev}
          disabled={!canGoPrev}
          aria-label={t('war_history_newer') || 'Newer war'}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* Right arrow — go to next (older) */}
        <button
          className={`war-swiper-arrow war-swiper-arrow-right${!canGoNext ? ' war-swiper-arrow-hidden' : ''}`}
          onClick={goNext}
          disabled={!canGoNext}
          aria-label={t('war_history_older') || 'Older war'}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        <div
          className="war-swiper-track"
          style={{ transform: trackTransform, transition: trackTransition }}
          onTransitionEnd={onTransitionEnd}
          onTouchStart={onPointerDown}
          onTouchMove={onPointerMove}
          onTouchEnd={onPointerUp}
          onMouseDown={onPointerDown}
        >
          {wars.map((war, i) => (
            <div className="war-swiper-slide" key={war.startTime || i}>
              <div className={`war-swiper-slide-inner${i < wars.length - 1 ? ' war-swiper-slide-gap' : ''}`}>
                <WarHeader t={t} war={war} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Progress dots */}
      <div className="war-swiper-dots">
        {wars.map((_, i) => (
          <button
            key={i}
            className={`war-swiper-dot${i === currentIndex ? ' active' : ''}`}
            onClick={() => goTo(i)}
            aria-label={i === 0 ? (t('war_history_current') || 'Current war') : `War ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
